import { useState } from 'react'
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react'

function statusBadge(status) {
  if (!status) return null
  const map = {
    BALANCED: 'bg-green-600 text-white',
    MINOR_VARIANCE: 'bg-amber-600 text-white',
    SHORTAGE: 'bg-red-600 text-white',
  }
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${map[status] || 'bg-gray-600 text-white'}`}>
      {status.replace('_', ' ')}
    </span>
  )
}

function formatR(n) {
  return `R${(Number(n) || 0).toFixed(2)}`
}

export default function TillHistory({ records, onDelete }) {
  const [expanded, setExpanded] = useState(null)

  const sorted = [...records].sort((a, b) => (b.date || '').localeCompare(a.date || ''))

  if (sorted.length === 0) {
    return <div className="text-center py-16 text-gray-600 text-sm">No till records saved yet</div>
  }

  return (
    <div className="space-y-2">
      {/* Desktop header */}
      <div className="hidden md:grid grid-cols-[100px_80px_80px_80px_90px_80px_90px_80px_90px_40px_40px] gap-2 text-xs text-gray-500 font-medium px-3">
        <span>Date</span>
        <span>Kitchen</span>
        <span>Bakery</span>
        <span>Tuck Shop</span>
        <span>Total Sales</span>
        <span>Cash Out</span>
        <span>Expected</span>
        <span>Variance</span>
        <span>Status</span>
        <span></span>
        <span></span>
      </div>

      {sorted.map(rec => {
        const isExpanded = expanded === rec.date
        const cashOutTotal = (rec.cashOutItems || []).reduce((s, r) => s + (Number(r.amount) || 0), 0)
        const expected = (Number(rec.grandTotal) || 0) - cashOutTotal
        const variance = expected - (Number(rec.actualCash) || 0)

        return (
          <div key={rec.date} className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
            {/* Row */}
            <div className="grid grid-cols-2 md:grid-cols-[100px_80px_80px_80px_90px_80px_90px_80px_90px_40px_40px] gap-2 items-center px-3 py-2.5 text-sm">
              <span className="text-white font-mono text-xs">{rec.date}</span>
              <span className="text-gray-300 text-xs">{formatR(rec.kitchen)}</span>
              <span className="text-gray-300 text-xs">{formatR(rec.bakery)}</span>
              <span className="text-gray-300 text-xs">{formatR(rec.tuckShop)}</span>
              <span className="text-white font-medium text-xs">{formatR(rec.grandTotal)}</span>
              <span className="text-red-400 text-xs">{formatR(cashOutTotal)}</span>
              <span className="text-brand-400 text-xs">{formatR(expected)}</span>
              <span className={`text-xs font-medium ${Math.abs(variance) <= 50 ? 'text-green-400' : variance > 0 ? 'text-amber-400' : 'text-red-400'}`}>
                {variance >= 0 ? '' : '-'}{formatR(Math.abs(variance))}
              </span>
              <span>{statusBadge(rec.status)}</span>
              <button
                onClick={() => setExpanded(isExpanded ? null : rec.date)}
                className="text-gray-500 hover:text-white p-1"
                title="Expand"
              >
                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              <button
                onClick={() => onDelete(rec.date)}
                className="text-gray-500 hover:text-red-400 p-1"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>

            {/* Expanded cash-out details */}
            {isExpanded && rec.cashOutItems && rec.cashOutItems.length > 0 && (
              <div className="border-t border-gray-800 px-4 py-3 bg-gray-950/50">
                <p className="text-xs text-gray-500 font-medium mb-2">Cash Out Line Items</p>
                <div className="space-y-1">
                  {rec.cashOutItems.map((item, i) => (
                    <div key={i} className="grid grid-cols-3 gap-4 text-xs">
                      <span className="text-gray-300">{item.description || '(no description)'}</span>
                      <span className="text-white">{formatR(item.amount)}</span>
                      <span className="text-gray-500">{item.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
